import { useLingui } from '@lingui/react/macro';
import { HamburgerMenu, Login, Logout, Wallet2 } from 'iconsax-reactjs';
import { type FC, useState } from 'react';
import { IdentificationBanner } from '@/account/kyc/IdentificationBanner';
import { useMe } from '@/account/profile/api';
import { createAuthNextPath, useHasAccessToken, useLogout } from '@/auth/api';
import { VERSION, WORKSPACE } from '@/base/constants';
import { Link, useLocation } from '@/base/router';
import { Gate } from '@/gate';
import { LanguageSelect } from '@/settings/LanguageSelect';
import { ThemeSelect } from '@/settings/ThemeSelect';
import { Avatar } from '@/shared/Avatar';
import { Button } from '@/shared/Button';
import { ClientOnly } from '@/shared/ClientOnly';
import { Dialog } from '@/shared/Dialog';
import { PopoverContent, PopoverRoot, PopoverTrigger } from '@/shared/Popover';
import { usePromise } from '@/shared/react';
import { useRuntimeEnv } from '@/shared/runtimeEnv';
import { useCopy } from '@/shared/useCopy';
import { Spotlight } from '@/spotlight/Spotlight';
import { HeaderNavLink } from './HeaderNavLink';
import { type HeaderLink, useHeaderLinks } from './useHeaderLinks';

const isExternal = (href?: string) => !!href?.startsWith('http');

const ProfilePopoverItem: FC<{ item: HeaderLink; onSelect: () => void }> = ({
  item,
  onSelect,
}) => {
  const path = Array.isArray(item.link) ? item.link[0] : item.link;
  const className =
    'text-sm text-content-primary flex items-center justify-between hover:text-content-link transition-colors cursor-pointer';
  const content = (
    <>
      <span>{item.label}</span>
      {item.badge}
    </>
  );
  if (isExternal(path))
    return (
      <a
        href={path}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onSelect}
      >
        {content}
      </a>
    );
  if (path)
    return (
      <Link
        workspace={Array.isArray(item.link) ? item.link[1] : undefined}
        to={path}
        className={className}
        onClick={onSelect}
      >
        {content}
      </Link>
    );
  return (
    <button
      type="button"
      className={className}
      onClick={() => {
        item.onClick?.();
        onSelect();
      }}
    >
      {content}
    </button>
  );
};

export const ProfileMenu: FC = () => {
  const hasAccessToken = useHasAccessToken();

  return (
    <ClientOnly>
      {hasAccessToken && (
        <div className="hidden desktop:contents">
          <DesktopProfileMenu />
        </div>
      )}
      <div className="contents desktop:hidden">
        <MobileProfileMenu />
      </div>
    </ClientOnly>
  );
};

const DesktopProfileMenu: FC = () => {
  const { t } = useLingui();
  const { isStandalone } = useRuntimeEnv();
  const me = useMe();
  const logout = useLogout();
  const [open, setOpen] = useState(false);
  const copyExternalId = useCopy(me.data?.external_id);
  const { children: copyIcon, ...copyButtonProps } = copyExternalId;
  const profileItems = useHeaderLinks().filter(
    (l) => l.level === 2 && !l.hidden,
  );

  const close = () => setOpen(false);

  const handleLogout = async () => {
    setOpen(false);
    await logout.trigger({});
  };

  const userLabel = me.data?.full_name || t`User`;

  return (
    <PopoverRoot open={open} onOpenChange={setOpen}>
      <Spotlight id="profile-menu">
        <PopoverTrigger className="h-8">
          <Button
            variant="transparent"
            type="div"
            size={8}
            loading={!me.data}
            fab
            className="!rounded-full shrink-0"
            aria-label="Profile Menu"
          >
            <Avatar
              seed={me.data?.username}
              className="size-full rounded-full"
            />
          </Button>
        </PopoverTrigger>
      </Spotlight>
      <PopoverContent
        align="end"
        side="bottom"
        className="w-[288px] min-h-max border-0 shadow-[0_16px_40px_rgba(12,12,12,0.12)] p-4"
      >
        <div className="flex gap-3">
          <Avatar
            seed={me.data?.username}
            className="size-10 rounded-full bg-background-disabled shrink-0"
          />
          <div className="flex flex-col gap-1 text-xs text-content-tertiary max-w-full overflow-hidden">
            {userLabel ? (
              <p className="text-base text-content-primary font-medium leading-tight truncate">
                {userLabel}
              </p>
            ) : null}
            {me.data?.external_id && (
              <div className="flex items-center gap-2">
                <span>
                  {t`UID:`} {me.data?.external_id ?? '—'}
                </span>
                <button
                  type="button"
                  className="text-content-tertiary hover:text-content-primary transition-colors cursor-pointer"
                  {...copyButtonProps}
                >
                  {copyIcon}
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="h-px bg-border-disabled my-4" />
        <div className="flex gap-2">
          {!isStandalone && (
            <Gate hooks={[Gate.hooks.hasFeature('deposit-iqd')]}>
              <Link
                workspace="app"
                to="/deposit?type=iqd"
                className="contents"
                onClick={() => setOpen(false)}
              >
                <Button variant="primary" size={10} className="grow">
                  {t`Deposit IQD`}
                </Button>
              </Link>
            </Gate>
          )}
          <Link
            workspace="app"
            to="/wallet"
            className="contents"
            onClick={() => setOpen(false)}
          >
            <Button variant="outline" size={10} className="grow">
              <Wallet2 size={16} />
              {t`Wallet`}
            </Button>
          </Link>
        </div>
        <div className="h-px bg-border-disabled my-4" />
        <div className="flex flex-col gap-3">
          {profileItems.map((item) => (
            <ProfilePopoverItem key={item.key} item={item} onSelect={close} />
          ))}
        </div>
        <div className="h-px bg-border-disabled my-4" />
        <p className="text-center text-content-tertiary text-xs pb-2">
          {VERSION ? `V ${VERSION}` : 'V —'}
        </p>
        <Button
          variant="ghost"
          size={10}
          block
          className="w-full"
          onClick={handleLogout}
        >
          <Logout size={18} />
          {t`Logout`}
        </Button>
      </PopoverContent>
    </PopoverRoot>
  );
};

const MobileProfileMenu: FC = () => {
  const hasAccessToken = useHasAccessToken();
  const me = useMe();
  const dialog = usePromise();

  return (
    <>
      <Spotlight id="profile-menu">
        {hasAccessToken ? (
          <Button
            variant="transparent"
            type="div"
            size={8}
            loading={!me.data}
            fab
            className="!rounded-full shrink-0"
            aria-label="Profile Menu"
            onClick={() => {
              dialog.run();
            }}
          >
            <Avatar
              seed={me.data?.username}
              className="size-full rounded-full"
            />
          </Button>
        ) : (
          <Button
            size={8}
            fab
            variant="outline"
            onClick={() => {
              dialog.run();
            }}
            className="shrink-0"
            aria-label="Burger Menu"
          >
            <HamburgerMenu />
          </Button>
        )}
      </Spotlight>
      <MobileProfileDialog
        open={dialog.isRunning}
        onClose={() => dialog.resolve(undefined)}
      />
    </>
  );
};

const MobileProfileDialog: FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => {
  const hasAccessToken = useHasAccessToken();
  const logout = useLogout();
  const [location, navigate] = useLocation();
  const { t } = useLingui();
  const me = useMe();
  const copyExternalId = useCopy(me.data?.external_id);
  const { children: copyIcon, ...copyButtonProps } = copyExternalId;
  const links = useHeaderLinks();
  const navLinks = links.filter((l) => l.level === 1 && !l.hidden);
  const profileLinks = links.filter((l) => l.level === 2 && !l.hidden);

  const handleAuthAction = async () => {
    if (hasAccessToken) {
      await logout.trigger({});
    } else {
      navigate('app', `/auth?next=${createAuthNextPath(WORKSPACE, location)}`);
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      position="bottom"
      className="pt-0 !h-max flex flex-col"
      headerClassName="pb-0 shrink-0"
      footerClassName="shrink-0"
      header={
        <div className="w-full">
          <div className="flex justify-start items-center gap-2 w-full">
            <div className="flex items-center gap-1 grow shrink">
              {hasAccessToken && (
                <Link to="/account" className="contents">
                  <Avatar
                    seed={me.data?.username}
                    className="size-6 rounded-full"
                  />
                  <p className="truncate text-sm">{me.data?.full_name ?? ''}</p>
                </Link>
              )}
            </div>
            <LanguageSelect
              mini
              size={8}
              arrow={false}
              className="w-auto px-2"
            />
            <ThemeSelect size={8} arrow={false} className="px-0 !w-8" mini />
          </div>
        </div>
      }
      footer={
        <div className="w-full space-y-3">
          <p className="text-center text-content-tertiary text-xs">
            {VERSION ? `V ${VERSION}` : 'V —'}
          </p>
          <Button
            block
            variant={hasAccessToken ? 'ghost' : 'primary'}
            className="w-full"
            size={12}
            onClick={handleAuthAction}
          >
            {hasAccessToken ? (
              <>
                <Logout />
                {t`Logout`}
              </>
            ) : (
              <>
                <Login />
                {t`Login / Sign Up`}
              </>
            )}
          </Button>
        </div>
      }
    >
      {me.data?.external_id && (
        <div className="flex items-center gap-2 my-1">
          <span className="text-content-tertiary text-xs">
            {t`UID:`} {me.data?.external_id ?? '—'}
          </span>
          <button
            type="button"
            className="text-content-tertiary hover:text-content-primary transition-colors cursor-pointer"
            {...copyButtonProps}
          >
            {copyIcon}
          </button>
        </div>
      )}

      <IdentificationBanner className="mb-3" />
      <div className="h-px bg-border-disabled my-3" />
      {hasAccessToken && (
        <>
          <div className="flex gap-2 items-center justify-stretch">
            <Gate hooks={[Gate.hooks.hasFeature('deposit-iqd')]}>
              <Link
                to="/deposit?type=iqd"
                className="contents"
                onClick={onClose}
              >
                <Button variant="primary" size={10} className="grow">
                  {t`Deposit IQD`}
                </Button>
              </Link>
            </Gate>
            <Link to="/wallet" className="contents" onClick={onClose}>
              <Button variant="outline" size={10} className="grow">
                <Wallet2 size={16} />
                {t`Wallet`}
              </Button>
            </Link>
          </div>

          <div className="h-px bg-border-disabled my-4" />
        </>
      )}

      <div className="flex flex-col gap-3 my-3">
        {navLinks.map((item) => (
          <HeaderNavLink
            key={item.key}
            item={item}
            mode="mobile"
            onNavigate={onClose}
          />
        ))}
        {profileLinks.length > 0 && <div className="h-px bg-border-disabled" />}
        {profileLinks.map((item) => (
          <HeaderNavLink
            key={item.key}
            item={item}
            mode="mobile"
            onNavigate={onClose}
          />
        ))}
      </div>
    </Dialog>
  );
};
